import React, { useState, useEffect } from 'react';
import { Edit, X, Save } from 'lucide-react';
import { Connection } from '../../types';

interface ConnectionManagerProps {
  connection: Connection;
  actions: {
    updateConnection: (id: string, updates: Partial<Connection>) => void;
  };
}

export const ConnectionManager: React.FC<ConnectionManagerProps> = ({
  connection,
  actions
}) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [editData, setEditData] = useState({
    name: connection.name || '',
    status: connection.status || 'ACTIVE'
  });

  // Réinitialiser les données quand la connexion change
  useEffect(() => {
    setEditData({
      name: connection.name || '',
      status: connection.status || 'ACTIVE'
    });
    setShowEditForm(false);
  }, [connection]);

  const handleEditConnection = () => {
    setShowEditForm(true);
  };

  const handleSaveConnection = async () => {
    console.log('🔗 ConnectionManager: Saving connection with data:', editData);
    
    // Créer la connexion mise à jour
    const updatedConnection: Partial<Connection> = {
      name: editData.name,
      status: editData.status
    };

    console.log('🔗 ConnectionManager: Calling updateConnection with:', connection.id, updatedConnection);

    // Mettre à jour la connexion dans l'état local
    actions.updateConnection(connection.id, updatedConnection);
    
    console.log('✏️ Modification d\'une connexion:', {
      connectionId: connection.id,
      name: editData.name,
      status: editData.status
    });
    
    // Fermer le formulaire
    setShowEditForm(false);
    
    console.log('✅ Connexion marquée comme à mettre à jour. Utilisez le bouton "Sauvegarder" de la toolbar pour sauvegarder.');
  };

  const handleCancelEdit = () => {
    setEditData({
      name: connection.name || '',
      status: connection.status || 'ACTIVE'
    });
    setShowEditForm(false);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'Actif';
      case 'INACTIVE': return 'Inactif';
      case 'MAINTENANCE': return 'Maintenance';
      case 'OFFLINE': return 'Hors ligne';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-500';
      case 'INACTIVE': return 'bg-gray-400';
      case 'MAINTENANCE': return 'bg-yellow-500';
      case 'OFFLINE': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };


  const getFieldLabel = (field: string) => {
    const labels: { [key: string]: string } = {
      'name': 'Nom du câble',
      'status': 'Statut',
      'length': 'Longueur'
    };
    return labels[field] || field;
  };

  const getFieldValue = (field: string) => {
    switch (field) {
      case 'name': return editData.name;
      case 'status': return editData.status;
      case 'length': return connection.length;
      default: return '';
    }
  };

  const updateFieldValue = (field: string, value: any) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const renderFormField = (field: string) => {
    const label = getFieldLabel(field);
    const value = getFieldValue(field);

    if (field === 'name') {
      return (
        <div key={field}>
          <label className="block text-xs text-blue-800 mb-1">{label}</label>
          <select
            value={value}
            onChange={(e) => updateFieldValue(field, e.target.value)}
            className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <optgroup label="BT câbles de réseau">
              <option value="3x150+70">3x150+70</option>
              <option value="3x150+70+2x16">3x150+70+2x16</option>
              <option value="3x16+54,6">3x16+54,6</option>
              <option value="3x16+54,6+2x16">3x16+54,6+2x16</option>
              <option value="3x35+54,6">3x35+54,6</option>
              <option value="3x35+54,6+2x16">3x35+54,6+2x16</option>
              <option value="3x50+54,6">3x50+54,6</option>
              <option value="3x50+54,6+2x16">3x50+54,6+2x16</option>
              <option value="3x70+54,6">3x70+54,6</option>
              <option value="3x70+54,6+1x16">3x70+54,6+1x16</option>
              <option value="3x70+54,6+2x16">3x70+54,6+2x16</option>
              <option value="3x95+54,6">3x95+54,6</option>
            </optgroup>
            <optgroup label="BT câbles de branchement">
              <option value="2x16">2x16</option>
              <option value="2x25">2x25</option>
              <option value="2x25+2x1.5">2x25+2x1.5</option>
              <option value="4x16">4x16</option>
              <option value="4x25">4x25</option>
              <option value="4x25+2x1,5">4x25+2x1,5</option>
            </optgroup>
          </select>
        </div>
      );
    }

    if (field === 'status') {
      return (
        <div key={field}>
          <label className="block text-xs text-blue-800 mb-1">{label}</label>
          <select
            value={value}
            onChange={(e) => updateFieldValue(field, e.target.value)}
            className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="ACTIVE">Actif</option>
            <option value="INACTIVE">Inactif</option>
            <option value="MAINTENANCE">Maintenance</option>
            <option value="OFFLINE">Hors ligne</option>
          </select>
        </div>
      );
    }

    return null;
  };

  const renderDisplayField = (field: string) => {
    const value = getFieldValue(field);

    if (field === 'status') {
      return (
        <div key={field}>
          <label className="block text-xs text-slate-600 mb-1">Statut</label>
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-800 px-2 py-1 bg-slate-100 rounded">
              {getStatusLabel(value as string)}
            </div>
            <span className={`w-2 h-2 rounded-full ${getStatusColor(value as string)}`}></span>
          </div>
        </div>
      );
    }

    if (field === 'name') {
      return (
        <div key={field}>
          <label className="block text-xs text-slate-600 mb-1">Nom du câble</label>
          <div className="text-xs text-slate-800 px-2 py-1 bg-slate-100 rounded">
            {value || 'Non défini'}
          </div>
        </div>
      );
    }

    if (field === 'length') {
      return (
        <div key={field}>
          <label className="block text-xs text-slate-600 mb-1">Longueur</label>
          <div className="text-xs text-slate-800 px-2 py-1 bg-slate-100 rounded">
            {value && typeof value === 'number' ? `${value.toFixed(2)} m` : 'Non calculée'}
          </div>
        </div>
      );
    }

    return null;
  };

  const connectionFields = ['name', 'status', 'length'];

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-800">Propriétés de la connexion</h3>
        <div className="flex gap-2">
          {!showEditForm ? (
            <button
              onClick={handleEditConnection}
              className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
              title="Modifier"
            >
              <Edit className="h-4 w-4" />
            </button>
          ) : (
            <>
              <button
                onClick={handleSaveConnection}
                className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors duration-200"
                title="Sauvegarder"
              >
                <Save className="h-4 w-4" />
              </button>
              <button
                onClick={handleCancelEdit}
                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                title="Annuler"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {showEditForm ? (
          connectionFields.filter(field => field !== 'length').map(field => renderFormField(field))
        ) : (
          connectionFields.map(field => renderDisplayField(field))
        )}
      </div>
    </div>
  );
};
